import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import type { FishingBrandDetail, FishingBrandListRow } from '../types';

const QK = ['admin', 'fishing-brands'] as const;

export function useFishingBrandsList(params: {
    search?: string;
    categorySlug?: string;
    countryIso?: string;
    status?: string;
    limit?: number;
    offset?: number;
}) {
    const {
        search,
        categorySlug,
        countryIso,
        status,
        limit = 50,
        offset = 0,
    } = params;

    return useQuery<{ rows: FishingBrandListRow[]; total: number }>({
        queryKey: [...QK, 'list', search, categorySlug, countryIso, status, limit, offset],
        queryFn: async () => {
            const rpcParams: Record<string, unknown> = {
                p_limit: limit,
                p_offset: offset,
            };
            if (search?.trim()) rpcParams.p_search = search.trim();
            if (categorySlug && categorySlug !== 'all') rpcParams.p_category_slug = categorySlug;
            if (countryIso && countryIso !== 'all') rpcParams.p_country_iso = countryIso;
            if (status && status !== 'all') rpcParams.p_status = status;
            const { data, error } = await dbClient.rpc('admin_list_fishing_brands', rpcParams);
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data) as FishingBrandListRow[];
            const total = Number(rows[0]?.total_count ?? 0);
            return { rows, total };
        },
        staleTime: 15_000,
    });
}

export function useFishingBrandFacets() {
    return useQuery<{ country_iso: string; country_name: string; brand_count: number | string }[]>({
        queryKey: [...QK, 'facets'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_fishing_brand_facets');
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as {
                country_iso: string;
                country_name: string;
                brand_count: number | string;
            }[];
        },
        staleTime: 60_000,
    });
}

export function useFishingBrandById(id: string | null) {
    return useQuery<FishingBrandDetail | null>({
        queryKey: [...QK, 'detail', id],
        queryFn: async () => {
            if (!id) return null;
            const { data, error } = await dbClient.rpc('admin_get_fishing_brand', { p_id: id });
            if (error) throw new Error(error.message);
            const rows = normalizeRpcResponse(data) as FishingBrandDetail[];
            return rows[0] ?? null;
        },
        enabled: Boolean(id) && id !== 'new',
        staleTime: 15_000,
    });
}

export function useUpsertFishingBrand() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (params: Record<string, unknown>) => {
            const { data, error } = await dbClient.rpc('admin_upsert_fishing_brand', params);
            if (error) throw new Error(error.message);
            const unwrapped = Array.isArray(data) ? data[0] : data;
            if (unwrapped && typeof unwrapped === 'object' && 'admin_upsert_fishing_brand' in unwrapped) {
                return String((unwrapped as { admin_upsert_fishing_brand: unknown }).admin_upsert_fishing_brand);
            }
            return String(unwrapped ?? '');
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK });
        },
    });
}

export function useBulkUpdateFishingBrands() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            ids: string[];
            action: 'publicar' | 'borrador' | 'categoria';
            category?: string;
        }) => {
            const { data, error } = await dbClient.rpc('admin_bulk_update_fishing_brands', {
                p_ids: input.ids,
                p_action: input.action,
                p_category: input.category ?? null,
            });
            if (error) throw new Error(error.message);
            return Number(data ?? 0);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK });
        },
    });
}

export function useUploadBrandLogo() {
    return useMutation({
        mutationFn: async ({ slug, file }: { slug: string; file: File }) => {
            const ext = (file.name.split('.').pop() || 'png').toLowerCase();
            if (ext === 'jpg' || ext === 'jpeg') {
                throw new Error('Usa PNG o SVG para conservar la transparencia');
            }
            const path = `marcas/${slug}.${ext}`;
            const result = await apiClient.uploadFile('brand-images', path, file);
            if (result.error) throw new Error(result.error.message);
            const url = result.data?.publicUrl || result.data?.url;
            if (!url) throw new Error('No se obtuvo URL del logo');
            return url;
        },
    });
}
